import { onColor } from '../constants/tokens';

/**
 * The CSS for a school's accent colour, as custom properties for both themes.
 *
 * A school picks one colour, which can be anything. Filled controls take it
 * as it is, with text in whichever of white or near-black reads on it. Text
 * and focus rings drawn on the canvas need contrast with the canvas, which
 * the colour may not have, a dark purple on the night canvas for one, so
 * those are moved toward the theme's ink until they do.
 *
 * Status colours are not touched. They are fixed by the Standard Response
 * Protocol.
 */

const DAY_CANVAS = '#eeefe9';
const DAY_INK = '#23251d';
const NIGHT_CANVAS = '#1a1c16';
const NIGHT_INK = '#fcfcfa';

const rgb = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const toHex = ([r, g, b]) => '#' + [r, g, b].map((c) => Math.round(c).toString(16).padStart(2, '0')).join('');

/** `a` moved `t` of the way to `b`. */
export const mix = (a, b, t) => {
  const [x, y] = [rgb(a), rgb(b)];
  return toHex(x.map((c, i) => c + (y[i] - c) * t));
};

const luminance = (hex) => {
  const [r, g, b] = rgb(hex).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/** WCAG contrast ratio of two colours. */
export const contrast = (a, b) => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};

/** `hex`, moved toward `ink` only as far as it takes to reach `ratio` against `canvas`. */
export const readable = (hex, canvas, ink, ratio) => {
  for (let t = 0; t <= 1; t += 0.05) {
    const candidate = mix(hex, ink, t);
    if (contrast(candidate, canvas) >= ratio) return candidate;
  }
  return ink;
};

export const isHex = (value) => typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value);

/**
 * The style sheet for `accent`, or an empty string for no brand.
 *
 * - `--sa-accent`, `--sa-on-accent`, `--sa-accent-hover`: filled controls,
 *   such as primary buttons.
 * - `--sa-accent-ring`: the edge of the selected pill.
 * - `--sa-link`: links in text (4.5:1 on the canvas).
 * - `--sa-focus`: focus outlines (3:1 on the canvas).
 * - `--sa-header`, `--sa-header-ink`, `--sa-header-border`: the header,
 *   filled with the colour, its text in white or near-black to suit.
 * - `--sa-header-ring`: a ring round the status dot, so a status the same
 *   colour as the school's, such as Hold on a purple header, still shows.
 */
export function brandCss(accent) {
  if (!isHex(accent)) return '';
  const hex = accent.toLowerCase();

  const theme = (canvas, ink, hover) => [
    '--sa-accent: ' + hex,
    '--sa-on-accent: ' + onColor(hex),
    '--sa-accent-hover: ' + hover,
    '--sa-accent-ring: ' + readable(hex, canvas, ink, 3),
    '--sa-link: ' + readable(hex, canvas, ink, 4.5),
    '--sa-focus: ' + readable(hex, canvas, ink, 3),
    '--sa-header: ' + hex,
    '--sa-header-ink: ' + onColor(hex, true),
    '--sa-header-border: ' + hex,
    '--sa-header-ring: ' + onColor(hex)
  ].join('; ');

  return ':root { ' + theme(DAY_CANVAS, DAY_INK, mix(hex, '#000000', 0.15)) + '; }\n' +
    ':root[data-theme=\'night\'] { ' + theme(NIGHT_CANVAS, NIGHT_INK, mix(hex, '#ffffff', 0.15)) + '; }';
}
